import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";

export default function AdminEmail() {
  const [recipient, setRecipient] = useState("");
  const [template, setTemplate] = useState("confirmation");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    if (!recipient) return toast.error("Enter a recipient email");
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { to: recipient, template });
      setLastResult({ ok: true, ...data });
      toast.success(`Sent! Email id: ${data.email_id?.slice(0, 8)}...`);
    } catch (err) {
      setLastResult({ ok: false, error: formatApiErrorDetail(err.response?.data?.detail) });
      toast.error("Failed — check Resend dashboard for details");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-4xl" data-testid="admin-email-page">
      <div className="mb-10">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2 flex items-center gap-2">
          <Mail className="h-3.5 w-3.5" /> Notifications
        </div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Email.</h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
          Test your transactional email setup. Real emails fire automatically when bookings are paid and when you schedule a session.
        </p>
      </div>

      {/* Status banner */}
      <div className="border border-amber-500/40 bg-amber-500/5 p-5 mb-8" data-testid="email-status-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-zinc-300 leading-relaxed">
            <p className="font-bold text-white mb-1">Domain verification required for delivery.</p>
            <p className="text-zinc-400">
              Emails will only reach inboxes from <code className="bg-zinc-900 px-1 text-[#FF331F]">coach@betterthroughboxing.com</code> after you verify the domain in Resend.
              Go to{" "}
              <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline inline-flex items-center gap-1">
                resend.com/domains <ExternalLink className="h-3 w-3" />
              </a>{" "}
              → Add Domain → enter <code className="bg-zinc-900 px-1">betterthroughboxing.com</code> → add the 3 DNS records (MX, TXT, TXT) to Squarespace Domains.
            </p>
          </div>
        </div>
      </div>

      <div className="border border-[#27272A] bg-[#0F0F0F] p-6 space-y-5">
        <h2 className="font-display uppercase text-xl tracking-widest text-white">Send test email</h2>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Template</Label>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="email-template-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
              <SelectItem value="confirmation">Client — Booking confirmation</SelectItem>
              <SelectItem value="coach">Coach — New booking notification</SelectItem>
              <SelectItem value="scheduled">Client — Session scheduled (with Zoom link)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Recipient email</Label>
          <Input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="your-email@example.com"
            className="bg-[#050505] border-[#27272A] rounded-sm"
            data-testid="email-recipient-input"
          />
          <p className="text-[11px] text-zinc-500">
            Before domain verification, Resend only delivers to the email you signed up with. After verification, you can send to any address.
          </p>
        </div>

        <Button
          onClick={send}
          disabled={sending}
          className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm"
          data-testid="send-test-email-btn"
        >
          <Send className="h-4 w-4 mr-2" /> {sending ? "Sending..." : "Send test email"}
        </Button>

        {lastResult && (
          <div className={`p-4 mt-2 border ${lastResult.ok ? "border-emerald-700 bg-emerald-950/40" : "border-red-700 bg-red-950/40"}`} data-testid="email-test-result">
            <div className={`flex items-center gap-2 text-sm font-bold uppercase tracking-widest ${lastResult.ok ? "text-emerald-400" : "text-red-400"}`}>
              {lastResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {lastResult.ok ? "Email accepted by Resend" : "Failed"}
            </div>
            {lastResult.ok && (
              <div className="text-xs text-zinc-400 mt-2 space-y-1">
                <div>Email ID: <code className="text-zinc-200">{lastResult.email_id}</code></div>
                <div>Template: <code className="text-zinc-200">{lastResult.template}</code></div>
                <div>To: <code className="text-zinc-200">{lastResult.to}</code></div>
                <div className="mt-2 text-[11px] text-zinc-500">
                  Check delivery status at{" "}
                  <a href="https://resend.com/emails" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline inline-flex items-center gap-1">
                    resend.com/emails <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            )}
            {!lastResult.ok && <div className="text-xs text-zinc-400 mt-2">{lastResult.error}</div>}
          </div>
        )}
      </div>

      <div className="mt-8 border border-[#27272A] bg-[#0F0F0F] p-6">
        <h3 className="font-display uppercase text-base tracking-widest text-white mb-3">Auto-triggers (production)</h3>
        <ul className="space-y-2 text-sm text-zinc-400">
          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Client receives <strong className="text-white">booking confirmation</strong> immediately after Stripe payment succeeds (webhook).</span></li>
          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> <span>You receive a <strong className="text-white">coach notification</strong> at <code className="bg-zinc-900 px-1 text-xs">theflowstatemethodllc@gmail.com</code> for every paid booking.</span></li>
          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" /> <span>Client receives <strong className="text-white">&ldquo;session scheduled&rdquo; + Zoom link</strong> whenever you set a date in the admin or calendar.</span></li>
        </ul>
      </div>
    </div>
  );
}
